import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../../theme";
import PersonIcon from "@mui/icons-material/Person";
import apiService from "../../../../../service/Api";

interface CrewMember {
  crew_member_id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  streams_attended: number;
}

interface CrewLowAttendanceResponse {
  threshold: number;
  count: number;
  results: CrewMember[];
}

const CrewAttendancePage = () => {
  const [members, setMembers] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLowAttendance = async () => {
      try {
        setLoading(true);

        const now = new Date();
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

        const res = await apiService.get<CrewLowAttendanceResponse>(
          `api/management/dashboard/crew-low-attendance/?month=${month}`,
        );
        setMembers(res.data?.results ?? []);
      } catch (err: unknown) {
        const errorMessage =
          (err as { message?: string })?.message ||
          "Failed to fetch crew attendance.";
        setError(errorMessage);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLowAttendance();
  }, []);

  return (
    <Box component="main" sx={{ minHeight: "100vh", mt: 2, p: 0 }}>
      <Box sx={{ maxWidth: 600 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && <CrewAttendanceCard members={members} />}
      </Box>
    </Box>
  );
};

const CrewAttendanceCard = ({ members }: { members: CrewMember[] }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 2, overflow: "hidden", bgcolor: colors.primary[900] }}
    >
      <Box sx={{ bgcolor: colors.primary[900], px: 2, py: 1.5 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.05em",
            color: "#424242",
            textTransform: "uppercase",
          }}
        >
          Crew Members That Have Attended {"<"}10 Streams
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {members.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "#9e9e9e", textAlign: "center", py: 2 }}
          >
            No crew members with low attendance.
          </Typography>
        ) : (
          members.map((member, index) => (
            <Box key={member.crew_member_id}>
              <MemberCard member={member} />
              {index < members.length - 1 && <Divider sx={{ my: 1 }} />}
            </Box>
          ))
        )}
      </Box>
    </Paper>
  );
};

const MemberCard = ({ member }: { member: CrewMember }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        borderRadius: 1,
        bgcolor: colors.primary[900],
        p: 1.5,
      }}
    >
      <Avatar sx={{ width: 40, height: 40, bgcolor: "#9e9e9e" }}>
        <PersonIcon sx={{ color: "#616161" }} />
      </Avatar>
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "#424242", textTransform: "uppercase" }}
        >
          {member.first_name} {member.last_name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "#757575",
            textTransform: "uppercase",
            letterSpacing: "0.03em",
          }}
        >
          Attended{" "}
          <Box component="span" sx={{ fontWeight: 700, color: "#d32f2f" }}>
            {member.streams_attended}
          </Box>{" "}
          streams this month
        </Typography>
      </Box>
    </Box>
  );
};

export default CrewAttendancePage;
